import React, { useEffect, useRef, useContext, useMemo } from "react";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

import { AdminDashboard, ManagerDashboard, ClientDashboard, ProviderDashboard, CompanyDashboard } from "./components";

type DashboardType = "admin" | "manager" | "client" | "provider";

/**
 * Determines the dashboard type based on user's accessLevel and role
 */
const getDashboardType = (user: any): DashboardType => {
  const accessLevel = user?.accessLevel;
  const roleTitle = user?.role?.title?.toLowerCase() || "";

  // Provider users get provider dashboard
  if (accessLevel === "isProvider") {
    return "provider";
  }

  // Client users get client dashboard
  if (accessLevel === "isClient" || accessLevel === "isClientAdmin") {
    return "client";
  }

  // Company users - check role to distinguish Admin vs Manager
  if (accessLevel === "isCompany" || accessLevel === "isSuper") {
    // Check if user has a Manager role
    if (roleTitle.includes("manager")) {
      return "manager";
    }
    // Default to admin dashboard for other company roles (Admin, etc.)
    return "admin";
  }

  // Fallback to admin
  return "admin";
};

/**
 * Get dashboard title based on type
 */
const getDashboardTitle = (type: DashboardType): string => {
  switch (type) {
    case "provider":
      return "Provider Dashboard";
    case "client":
      return "Client Dashboard";
    case "manager":
      return "Manager Dashboard";
    default:
      return "Dashboard";
  }
};

/**
 * Get dashboard subtitle based on type
 */
const getDashboardSubtitle = (type: DashboardType, user: any): string => {
  const firstName = user?.firstName || "";
  
  switch (type) {
    case "provider":
      return `Welcome back${firstName ? `, ${firstName}` : ""}. Here's an overview of your purchase orders and deliveries.`;
    case "client":
      return `Welcome back${firstName ? `, ${firstName}` : ""}. Here's an overview of your projects.`;
    case "manager":
      return `Welcome back${firstName ? `, ${firstName}` : ""}. Here's an overview of scheduling and operations.`;
    default:
      return `Welcome back${firstName ? `, ${firstName}` : ""}. Here's an overview of your business.`;
  }
};

const Dashboard = () => {
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  // Determine dashboard type based on user
  const dashboardType = useMemo(() => getDashboardType(user), [user?.accessLevel, user?.role?.title]);

  useEffect(() => {
    const title = getDashboardTitle(dashboardType);
    document.title = `${process.env.REACT_APP_NAME} | ${title}`;
  }, [dashboardType]);

  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    void trackActivity({
      module: "dashboard",
      action: "view_dashboard",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: { dashboardType },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user?.id, user?.company?.id, dashboardType]);

  // Render the appropriate dashboard based on user type
  const renderDashboard = () => {
    switch (dashboardType) {
      case "provider":
        return <ProviderDashboard />;
      case "client":
        return <ClientDashboard />;
      case "manager":
      default:
        // Admin + Manager both render CompanyDashboard, which composes
        // widgets from role permissions. Falls back to "all widgets"
        // for existing roles that have no widget permissions set.
        return <CompanyDashboard />;
    }
  };

  return (
    <>
      <Header />
      <Page 
        title={getDashboardTitle(dashboardType)} 
        subtitle={getDashboardSubtitle(dashboardType, user)}
      >
        {renderDashboard()}
      </Page>
    </>
  );
};

export default Dashboard;
